/**
 * SettingsTab — Phase 12 Schwellen-Editor.
 *
 * Tabellen-Layout mit 2 Sektionen (DHL + Lieferanten). Inline-Edit pro Zeile,
 * eigener Save-Button. View-only-User sehen Felder disabled + Hinweis-Banner.
 *
 * Permission: manage:auftragsverfolgung (aus useAuthStore).
 * Admin-Role impliziert manage:* (hasPermission prueft das bereits).
 *
 * CI-Farben: Save #E9FF57 + #1A1A1A, Border #DAD9D9.
 * Toast: lokal + 3s-setTimeout (kein externes Toast-System).
 */

import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Save, Settings as SettingsIcon } from 'lucide-react';
import { useAuthStore } from '@libero/core/stores/authStore';
import { getThresholds, updateThreshold, type ThresholdRow } from '../lib/thresholdsApi';
import { formatDurationHuman } from '../lib/stuckInfoFormatter';
import AbholbereitMailPanel from './AbholbereitMailPanel';

/** Erklaerung pro Schwellen-Key (DHL-Status / Lieferant) — wird unter der jeweiligen Tabelle gerendert. */
const THRESHOLD_HINTS: Record<string, string> = {
  // DHL — Quelle: TrackingEvent.internal_status (gemappt aus DHL-Carrier-Status)
  'dhl.exception':
    'DHL hat eine Stoerung gemeldet (z.B. Annahmeverweigerung, Adresse falsch, Lagerung wegen Nichtzustellbarkeit).',
  'dhl.in_transit':
    'Paket ist abgeholt und im Transport — bewegt sich von Sortierzentrum zu Sortierzentrum.',
  'dhl.out_for_delivery':
    'Paket ist heute auf dem Zustellfahrzeug — sollte am gleichen Tag zugestellt werden.',
  'dhl.pre_transit':
    'Versandlabel wurde gedruckt, aber DHL hat das Paket noch nicht im System (z.B. Paket nicht uebergeben).',
  'dhl.returned':
    'Paket wird zurueck an den Absender geschickt.',
  'dhl.unknown':
    'DHL liefert keinen Status — Paketnummer ungueltig oder bei DHL nicht bekannt.',
  'dhl.delivered':
    'Paket wurde laut DHL zugestellt.',
  // Lieferanten — Status-IDs aus Plenty
  'supplier.huck':
    'Plenty-Status 5.50 "LS Huck" — Lieferschein an Huck rausgegangen, wartet auf Versand-Bestaetigung.',
  'supplier.helo':
    'Plenty-Status 5.60 "LS Helo" — Lieferschein an Helo rausgegangen.',
  'supplier.glawel':
    'Plenty-Status 5.65 "LS GLA-WEL" — Lieferschein an GlaWel rausgegangen (lange Lieferzeiten — Stahl-Spedition).',
  'supplier.sporttore':
    'Plenty-Status 5.66 "LS Sporttore" — Lieferschein an Sporttore rausgegangen.',
};

// Erweitert ThresholdRow um Edit-State pro Zeile
interface EditRow extends ThresholdRow {
  isDirty: boolean;
  isSaving: boolean;
  errorMessage: string | null;
}

type Toast = { type: 'success' | 'error'; text: string } | null;

export default function SettingsTab() {
  const [rows, setRows] = useState<EditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast>(null);

  // Permission-Check via Zustand Auth-Store (kein extra API-Call noetig)
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canManage = hasPermission('manage:auftragsverfolgung');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await getThresholds();
      setRows(
        data.map((r) => ({ ...r, isDirty: false, isSaving: false, errorMessage: null })),
      );
    } catch (err: unknown) {
      const e = err as { message?: string };
      setLoadError(e?.message ?? 'Schwellen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function patchRow(key: string, patch: Partial<EditRow>) {
    setRows((prev) =>
      prev.map((r) => (r.key === key ? { ...r, ...patch, isDirty: true } : r)),
    );
  }

  async function saveRow(key: string) {
    const row = rows.find((r) => r.key === key);
    if (!row) return;

    // Client-seitige Validation (Backend validiert ebenfalls)
    if (!Number.isInteger(row.thresholdHours) || row.thresholdHours < 0) {
      patchRow(key, { errorMessage: 'Alarm-Stunden muss eine ganze Zahl >= 0 sein', isDirty: true });
      return;
    }
    if (
      row.thresholdWarnHours != null &&
      (!Number.isInteger(row.thresholdWarnHours) || row.thresholdWarnHours < 0)
    ) {
      patchRow(key, { errorMessage: 'Warn-Stunden muss leer oder eine ganze Zahl >= 0 sein', isDirty: true });
      return;
    }

    patchRow(key, { isSaving: true, errorMessage: null });
    try {
      await updateThreshold(key, {
        threshold_hours: row.thresholdHours,
        threshold_warn_hours: row.thresholdWarnHours,
        alert_email_enabled: row.alertEmailEnabled,
        is_active: row.isActive,
      });
      setRows((prev) =>
        prev.map((r) =>
          r.key === key ? { ...r, isDirty: false, isSaving: false, errorMessage: null } : r,
        ),
      );
      setToast({ type: 'success', text: `Schwelle "${row.label}" gespeichert` });
      setTimeout(() => setToast(null), 3000);
    } catch (err: unknown) {
      const e = err as { message?: string };
      patchRow(key, {
        isSaving: false,
        errorMessage: e?.message ?? 'Speichern fehlgeschlagen',
        isDirty: true,
      });
    }
  }

  const dhlRows = rows.filter((r) => r.category === 'dhl');
  const supplierRows = rows.filter((r) => r.category === 'supplier');

  return (
    <div className="space-y-6 p-2">
      {/* Seiten-Titel */}
      <div className="flex items-center gap-2">
        <SettingsIcon className="w-5 h-5 text-[#1A1A1A]" />
        <h2 className="text-lg font-semibold text-[#1A1A1A]">Stuck-Schwellen</h2>
      </div>

      {/* View-only-Banner */}
      {!canManage && (
        <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-900">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            Du hast nur Lesezugriff. Einstellungen koennen nur von Admins oder
            Programm-Eigentuemern (Permission{' '}
            <code className="text-xs font-mono bg-yellow-100 px-1 rounded">
              manage:auftragsverfolgung
            </code>
            ) geaendert werden.
          </div>
        </div>
      )}

      {/* Lade-State */}
      {loading && (
        <div className="text-sm text-[#1A1A1A]">Laedt Schwellen…</div>
      )}

      {/* Fehler */}
      {loadError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
          Fehler: {loadError}
        </div>
      )}

      {/* Tabellen */}
      {!loading && !loadError && (
        <>
          <ThresholdSection
            title="DHL-Tracking-Schwellen"
            rows={dhlRows}
            canManage={canManage}
            onPatch={patchRow}
            onSave={saveRow}
          />
          <ThresholdSection
            title="Lieferanten-Schwellen"
            rows={supplierRows}
            canManage={canManage}
            onPatch={patchRow}
            onSave={saveRow}
          />
        </>
      )}

      {/* 26.08.2026: Abholbereit-Kundenmail — manueller Start (Testphase) */}
      <AbholbereitMailPanel />

      {/* Toast (fixed rechts unten, 3s) */}
      {toast && (
        <div
          role="status"
          className={`fixed bottom-6 right-6 px-4 py-2 rounded shadow-lg text-sm z-50 ${
            toast.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}

// ─── ThresholdSection ────────────────────────────────────────────────────────

interface SectionProps {
  title: string;
  rows: EditRow[];
  canManage: boolean;
  onPatch: (key: string, patch: Partial<EditRow>) => void;
  onSave: (key: string) => Promise<void>;
}

function ThresholdSection({ title, rows, canManage, onPatch, onSave }: SectionProps) {
  if (rows.length === 0) return null;

  return (
    <div>
      <h3 className="text-sm font-semibold text-[#1A1A1A] mb-2">{title}</h3>
      <div className="border border-[#DAD9D9] rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F5F5] text-[#1A1A1A]">
            <tr>
              <th className="text-left px-3 py-2 font-semibold w-40">Status</th>
              <th className="text-left px-3 py-2 font-semibold w-44">Warnung (h)</th>
              <th className="text-left px-3 py-2 font-semibold w-44">Alarm (h)</th>
              <th className="text-left px-3 py-2 font-semibold">Email</th>
              <th className="text-left px-3 py-2 font-semibold">Aktiv</th>
              <th className="text-left px-3 py-2 font-semibold">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-[#DAD9D9] align-top">
                {/* Label + Erklaerungs-Text */}
                <td className="px-3 py-2 text-[#1A1A1A]">
                  <div className="font-medium">{r.label}</div>
                  {THRESHOLD_HINTS[r.key] && (
                    <div className="text-xs text-gray-500 mt-1 leading-snug">
                      {THRESHOLD_HINTS[r.key]}
                    </div>
                  )}
                </td>

                {/* Warnung (h) — mit Tages/Wochen-Hinweis */}
                <td className="px-3 py-2">
                  <input
                    type="number"
                    min={0}
                    disabled={!canManage || r.isSaving}
                    value={r.thresholdWarnHours ?? ''}
                    onChange={(e) =>
                      onPatch(r.key, {
                        thresholdWarnHours:
                          e.target.value === '' ? null : parseInt(e.target.value, 10),
                      })
                    }
                    className="w-20 px-2 py-1 border border-[#DAD9D9] rounded text-[#1A1A1A] text-sm disabled:bg-[#F5F5F5] disabled:cursor-not-allowed"
                    placeholder="–"
                  />
                  {r.thresholdWarnHours != null && r.thresholdWarnHours > 0 && (
                    <div className="text-xs text-gray-500 mt-0.5">
                      ≈ {formatDurationHuman(r.thresholdWarnHours)}
                    </div>
                  )}
                </td>

                {/* Alarm (h) — mit Tages/Wochen-Hinweis */}
                <td className="px-3 py-2">
                  <input
                    type="number"
                    min={0}
                    disabled={!canManage || r.isSaving}
                    value={r.thresholdHours}
                    onChange={(e) =>
                      onPatch(r.key, {
                        thresholdHours: parseInt(e.target.value, 10) || 0,
                      })
                    }
                    className="w-20 px-2 py-1 border border-[#DAD9D9] rounded text-[#1A1A1A] text-sm disabled:bg-[#F5F5F5] disabled:cursor-not-allowed"
                  />
                  {r.thresholdHours > 0 && (
                    <div className="text-xs text-gray-500 mt-0.5">
                      ≈ {formatDurationHuman(r.thresholdHours)}
                    </div>
                  )}
                </td>

                {/* Email-Toggle */}
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    disabled={!canManage || r.isSaving}
                    checked={r.alertEmailEnabled}
                    onChange={(e) => onPatch(r.key, { alertEmailEnabled: e.target.checked })}
                    className="w-4 h-4 disabled:cursor-not-allowed"
                    title="Email-Benachrichtigung"
                  />
                </td>

                {/* Aktiv-Toggle */}
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    disabled={!canManage || r.isSaving}
                    checked={r.isActive}
                    onChange={(e) => onPatch(r.key, { isActive: e.target.checked })}
                    className="w-4 h-4 disabled:cursor-not-allowed"
                    title="Schwelle aktiv"
                  />
                </td>

                {/* Save-Button + Inline-Error */}
                <td className="px-3 py-2">
                  {canManage && (
                    <button
                      type="button"
                      onClick={() => onSave(r.key)}
                      disabled={!r.isDirty || r.isSaving}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-[#E9FF57] text-[#1A1A1A] rounded text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#d4ea4d] transition-colors"
                    >
                      <Save className="w-3 h-3" />
                      {r.isSaving ? 'Speichere…' : 'Speichern'}
                    </button>
                  )}
                  {r.errorMessage && (
                    <div className="text-xs text-red-700 mt-1 max-w-[200px]">
                      {r.errorMessage}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
